import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import { useTranslation } from 'react-i18next';
import BoxData from '../../../../components/boxData';
import Toggle from '../../../../components/toggle';
import { rankAPI, userAPI } from '../../../../actions/imsServer';
import { useLevels } from '../../../../context/levels';
import ProgressBar from '../../../../components/charts/progressBar';
import { Favorite, FavoriteCheck, OrganizationEmblem } from '../../../../assets/images';
import Popup from '../../../../components/popup';
import { toDisplayString } from '../../../../utils/base';
import { RANKING_TYPE_TOGGLE_OPTIONS } from '../../../../constants';

// Styled components
const PaperCard = styled(Box)(({ theme, background }) => ({
    borderRadius: '20px',
    padding: '20px',
    boxShadow: theme.palette.shadows?.custom?.card || '0px 2px 8px rgba(0, 0, 0, 0.1)',
    border: 'none',
    position: 'relative',
    background: background || theme.palette.figma.block,
}));

const RankingCard = ({
    userData = null,
    membership = null,
    sx = {},
    background = null,
    showDeleteButton = false,
    onDelete = null,
    onRefresh = null, // 데이터 새로고침 콜백
    toggleActive: externalToggleActive = null, // 외부에서 전달받은 toggleActive (null이면 내부 state 사용)
    hideToggle = false // 토글 숨김 여부
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [internalToggleActive, setInternalToggleActive] = useState(0);
    const toggleActive = externalToggleActive !== null ? externalToggleActive : internalToggleActive;
    const [monthlyRanksSectionData, setMonthlyRanksSectionData] = useState(null);
    const [monthlyRanksDivisionData, setMonthlyRanksDivisionData] = useState(null);
    const [monthlyRanksOrganizationData, setMonthlyRanksOrganizationData] = useState(null);
    const [monthlyRanksData, setMonthlyRanksData] = useState(null);
    const [ranksSectionData, setRanksSectionData] = useState(null);
    const [ranksDivisionData, setRanksDivisionData] = useState(null);
    const [ranksOrganizationData, setRanksOrganizationData] = useState(null);
    const [ranksData, setRanksData] = useState(null);
    const [data, setData] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');
    const [isConfirmPopup, setIsConfirmPopup] = useState(false);
    const { organizationLevels } = useLevels();
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const currentMonthYear = currentYear + '-' + currentMonth.toString().padStart(2, '0');

    // 상위 퍼센트 계산 함수 (NaN 처리 포함)
    const calculateTopPercentage = (total, rank) => {
        if (!total || !rank) return '-';
        const percentage = 100 - ((total - rank + 1) / total * 100);
        if (isNaN(percentage)) return '-';
        return percentage.toFixed(2);
    };

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '', isConfirm = false) => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setIsConfirmPopup(isConfirm);
        setPopupOpen(true);
    };

    // 팝업 닫기 함수
    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    // 메인 기관 변경 확인 팝업 표시
    const handleFavoriteClick = () => {
        if (!userData || !membership?.organizationId) return;

        const message = t('ranking_card_main_org_change_confirm_message');
        showPopup(message, '', true);
    };

    // 메인 기관 변경 실행
    const handleConfirmChangeMainOrganization = async () => {
        if (!userData || !membership?.organizationId) return;

        handleClosePopup();

        try {
            const updateUserResponse = await userAPI.updateUser({
                id: userData.id,
                mainOrganizationId: membership.organizationId
            });

            if (updateUserResponse?.message === 'main_organization_change_limit_per_month') {
                showPopup(t('main_organization_has_already_been_changed_once_this_month_please_change_it_next_month'));
            } else {
                // 사용자 정보 다시 가져와서 localStorage에 저장
                try {
                    const userResponse = await userAPI.getMyInfo();
                    const updatedUserData = userResponse?.data?.user;
                    if (updatedUserData) {
                        const currentMeInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');
                        const newMeInfo = { ...currentMeInfo, ...updatedUserData };
                        localStorage.setItem('meInfo', JSON.stringify(newMeInfo));
                    }
                } catch (err) {
                    console.error('Failed to refresh user info:', err);
                }

                showPopup(t('main_organization_has_been_changed'));

                // 부모 컴포넌트에 새로고침 요청
                if (onRefresh) {
                    onRefresh();
                }
            }
        } catch (error) {
            console.error('Failed to update main organization:', error);

            if (error.response?.data?.message === 'main_organization_change_limit_per_month') {
                showPopup(t('main_organization_has_already_been_changed_once_this_month_please_change_it_next_month'));
            }
        }
    };

    const fetchMonthlyRanksSection = async () => {
        if (!userData?.id) return;
        const monthlyRanksSectionResponse = await rankAPI.getMonthlyRanks({ month: currentMonthYear, userId: userData.id, organizationId: membership?.organizationId, division: membership?.division, section: membership?.section });
        if (monthlyRanksSectionResponse?.data) {
            setMonthlyRanksSectionData(monthlyRanksSectionResponse.data);
        }
    }

    const fetchMonthlyRanksDivision = async () => {
        if (!userData?.id) return;
        const monthlyRanksDivisionResponse = await rankAPI.getMonthlyRanks({ month: currentMonthYear, userId: userData.id, organizationId: membership?.organizationId, division: membership?.division });
        if (monthlyRanksDivisionResponse?.data) {
            setMonthlyRanksDivisionData(monthlyRanksDivisionResponse.data);
        }
    }

    const fetchMonthlyRanksOrganization = async () => {
        if (!userData?.id) return;
        const monthlyRanksOrganizationResponse = await rankAPI.getMonthlyRanks({ month: currentMonthYear, userId: userData.id, organizationId: membership?.organizationId });
        if (monthlyRanksOrganizationResponse?.data) {
            setMonthlyRanksOrganizationData(monthlyRanksOrganizationResponse.data);
        }
    }

    const fetchMonthlyRanks = async () => {
        if (!userData?.id) return;
        const monthlyRanksNationalResponse = await rankAPI.getMonthlyRanks({ month: currentMonthYear, userId: userData.id });
        if (monthlyRanksNationalResponse?.data) {
            setMonthlyRanksData(monthlyRanksNationalResponse.data);
        }
    }
    const fetchRanksOrganization = async () => {
        if (!userData?.id) return;
        const ranksOrganizationResponse = await rankAPI.getRanks({ userId: userData.id, organizationId: membership?.organizationId });
        if (ranksOrganizationResponse?.data) {
            setRanksOrganizationData(ranksOrganizationResponse.data);
        }
    }

    const fetchRanksSection = async () => {
        if (!userData?.id) return;
        const ranksSectionResponse = await rankAPI.getRanks({ userId: userData.id, organizationId: membership?.organizationId, division: membership?.division, section: membership?.section });
        if (ranksSectionResponse?.data) {
            setRanksSectionData(ranksSectionResponse.data);
        }
    }

    const fetchRanksDivision = async () => {
        if (!userData?.id) return;
        const ranksDivisionResponse = await rankAPI.getRanks({ userId: userData.id, organizationId: membership?.organizationId, division: membership?.division });
        if (ranksDivisionResponse?.data) {
            setRanksDivisionData(ranksDivisionResponse.data);
        }
    }

    const fetchRanks = async () => {
        if (!userData?.id) return;
        const ranksResponse = await rankAPI.getRanks({ userId: userData.id });
        if (ranksResponse?.data) {
            setRanksData(ranksResponse.data);
        }
    }

    useEffect(() => {
        if (!userData?.id) return;

        // 항상 fetch하는 것들
        fetchMonthlyRanksOrganization();
        fetchMonthlyRanks();
        fetchRanksOrganization();
        fetchRanks();

        // 조건부 fetch
        if (membership?.section) {
            fetchMonthlyRanksSection();
            fetchRanksSection();
        }

        if (membership?.division) {
            fetchMonthlyRanksDivision();
            fetchRanksDivision();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch 함수는 매 렌더 새 참조라 의존성에서 제외(반복 호출 방지)
    }, [userData?.id, membership?.section, membership?.division, membership?.organizationId]);

    // toggleActive에 따라 데이터 설정
    useEffect(() => {
        if (toggleActive === 0) {
            // 이달 랭킹
            if (membership?.section) {
                // section이 있는 경우: division, organization, national 표시
                setData([
                    {
                        label: membership?.division,
                        data: monthlyRanksDivisionData,
                    },
                    {
                        label: t('whole_school'),
                        data: monthlyRanksOrganizationData,
                    },
                    {
                        label: t('nationwide'),
                        data: monthlyRanksData,
                    },
                ]);
            } else if (membership?.division) {
                // section이 없고 division만 있는 경우: organization, national만 표시
                setData([
                    {
                        label: t('whole_school'),
                        data: monthlyRanksOrganizationData,
                    },
                    {
                        label: t('nationwide'),
                        data: monthlyRanksData,
                    },
                ]);
            } else {
                // section도 division도 없는 경우: national만 표시
                setData([
                    {
                        label: t('nationwide'),
                        data: monthlyRanksData,
                    },
                ]);
            }
        } else {
            // 누적 랭킹
            if (membership?.section) {
                // section이 있는 경우: division, organization, national 표시
                setData([
                    {
                        label: membership?.division,
                        data: ranksDivisionData,
                    },
                    {
                        label: t('whole_school'),
                        data: ranksOrganizationData,
                    },
                    {
                        label: t('nationwide'),
                        data: ranksData,
                    },
                ]);
            } else if (membership?.division) {
                // section이 없고 division만 있는 경우: organization, national만 표시
                setData([
                    {
                        label: t('whole_school'),
                        data: ranksOrganizationData,
                    },
                    {
                        label: t('nationwide'),
                        data: ranksData,
                    },
                ]);
            } else {
                // section도 division도 없는 경우: national만 표시
                setData([
                    {
                        label: t('nationwide'),
                        data: ranksData,
                    },
                ]);
            }
        }
    }, [t, toggleActive, monthlyRanksSectionData, monthlyRanksDivisionData, monthlyRanksOrganizationData, monthlyRanksData, ranksSectionData, ranksDivisionData, ranksOrganizationData, ranksData, membership?.division, membership?.section]);

    return (
        <>
            <PaperCard sx={{ minHeight: '382px', ...sx }} background={background}>
                {/* 삭제 버튼 */}
                {showDeleteButton && (
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onDelete) onDelete();
                        }}
                        sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            minWidth: 'auto',
                            width: '24px',
                            height: '24px',
                            padding: 0,
                            backgroundColor: 'transparent',
                            color: theme.palette.figma.white[60],
                            fontSize: '16px',
                            fontWeight: 400,
                            borderRadius: '50%',
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: theme.palette.figma.white[20],
                                color: theme.palette.figma.Red_didim,
                            }
                        }}
                    >
                        ×
                    </Button>
                )}
                {/* 학교 레벨 섹션 */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        alignSelf: 'stretch',
                        mb: 2.5,
                        position: 'relative'
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '-10px',
                            left: '-10px',
                            zIndex: 1
                        }}
                    >
                        {(userData?.mainOrganizationId) === membership?.organizationId ? (
                            <Box sx={{ cursor: 'not-allowed' }}>
                                <FavoriteCheck
                                    size={20}
                                    opacity={1}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        opacity: 0.8
                                    }
                                }}
                                onClick={handleFavoriteClick}
                            >
                                <Favorite
                                    size={20}
                                    opacity={1}
                                />
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <OrganizationEmblem
                            level={membership?.organization?.level || 1}
                            size={70}
                        />
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '5px',
                            flex: 1
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma.white[80],
                                letterSpacing: '-0.48px',
                                textAlign: 'left',
                                height: '19px',
                            }}
                        >
                            {membership?.organization?.name}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '5px'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '30px',
                                    fontWeight: 700,
                                    color: theme.palette.text.main,
                                    letterSpacing: '-0.9px',
                                    textAlign: 'left',
                                    height: '36px',
                                }}
                            >
                                LV.{membership?.organization?.level || 1}
                            </Typography>
                            {(() => {
                                const currentLevel = membership?.organization?.level || 1;
                                const currentExp = membership?.organization?.exp || 0;
                                let levelMinExp = 0;
                                let levelMaxExp = 10000;

                                if (Array.isArray(organizationLevels) && organizationLevels.length > 0) {
                                    const currentLevelData = organizationLevels.find(l => l.level === currentLevel);
                                    const prevLevelData = organizationLevels.find(l => l.level === currentLevel - 1);

                                    if (currentLevelData) {
                                        // min: 이전 레벨의 totalExp (현재 레벨 시작점)
                                        // 레벨 1인 경우 이전 레벨이 없으므로 0
                                        levelMinExp = prevLevelData?.totalExp || 0;
                                        // max: 현재 레벨의 totalExp (다음 레벨 시작점 = 현재 레벨 목표)
                                        levelMaxExp = currentLevelData.totalExp || (levelMinExp + 10000);
                                    } else {
                                        // 현재 레벨 데이터를 찾지 못한 경우 기본값 사용
                                        levelMinExp = currentExp;
                                        levelMaxExp = currentExp + 10000;
                                    }
                                } else {
                                    // organizationLevels가 없거나 로딩 중일 경우 기본값 사용
                                    levelMinExp = currentExp;
                                    levelMaxExp = currentExp + 10000;
                                }

                                return (
                                    <ProgressBar
                                        rangeStart={levelMinExp}
                                        rangeEnd={levelMaxExp}
                                        start={levelMinExp}
                                        end={currentExp}
                                        current={currentExp}
                                        height="20px"
                                        backgroundColor={theme.palette.figma.white[20]}
                                        showCurrentIndicator={false}
                                        progressColor={theme.palette.figma.steps}
                                        sx={{ alignSelf: 'stretch' }}
                                    />
                                );
                            })()}
                        </Box>
                    </Box>

                </Box>

                {/* 구분선 */}
                <Box
                    sx={{
                        height: '1px',
                        backgroundColor: theme.palette.figma.white[100],
                        width: '100%',
                        mb: 2.5
                    }}
                />

                {/* My Rank 섹션 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5,
                        width: '100%'
                    }}
                >
                    {/* My Rank 헤더 */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2.5,
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma.white[60],
                                letterSpacing: '-0.48px',
                                flex: 1,
                                textAlign: 'left'
                            }}
                        >
                            {t('my_rank')}
                        </Typography>
                        {!hideToggle && (
                            <Toggle
                                options={[...RANKING_TYPE_TOGGLE_OPTIONS].reverse().map(o => t(o.label))}
                                value={toggleActive}
                                onChange={setInternalToggleActive}
                                size="small"
                                sx={{ width: 'auto', minWidth: '200px' }}
                            />
                        )}
                    </Box>

                    {/* 순위 표시 */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2.5,
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '60px',
                                    fontWeight: 600,
                                    color: theme.palette.text.main,
                                    letterSpacing: '-1.8px',
                                    lineHeight: 1
                                }}
                            >
                                {toggleActive === 0
                                    ? (membership?.section
                                        ? monthlyRanksSectionData?.ranks[0]?.rank?.toLocaleString() || '-'
                                        : membership?.division
                                            ? monthlyRanksDivisionData?.ranks[0]?.rank?.toLocaleString() || '-'
                                            : monthlyRanksOrganizationData?.ranks[0]?.rank?.toLocaleString() || '-')
                                    : (membership?.section
                                        ? ranksSectionData?.ranks[0]?.rank?.toLocaleString() || '-'
                                        : membership?.division
                                            ? ranksDivisionData?.ranks[0]?.rank?.toLocaleString() || '-'
                                            : ranksOrganizationData?.ranks[0]?.rank?.toLocaleString() || '-')}
                            </Typography>
                            {t('ranking_card_rank_suffix') ? (
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        color: theme.palette.figma.white[80],
                                        letterSpacing: '-0.54px',
                                        height: '50px',
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        width: '16px'
                                    }}
                                >
                                    {t('ranking_card_rank_suffix')}
                                </Typography>
                            ) : null}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25,
                                flex: 1
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: theme.palette.figma.white[80],
                                        letterSpacing: '-0.48px'
                                    }}
                                >
                                    {membership?.section || membership?.division || t('whole_school')}
                                </Typography>
                                <BoxData
                                    data={toggleActive === 0
                                        ? (membership?.section
                                            ? t('top_percent', { percent: calculateTopPercentage(monthlyRanksSectionData?.total, monthlyRanksSectionData?.ranks?.[0]?.rank) })
                                            : membership?.division
                                                ? t('top_percent', { percent: calculateTopPercentage(monthlyRanksDivisionData?.total, monthlyRanksDivisionData?.ranks?.[0]?.rank) })
                                                : t('top_percent', { percent: calculateTopPercentage(monthlyRanksOrganizationData?.total, monthlyRanksOrganizationData?.ranks?.[0]?.rank) }))
                                        : (membership?.section
                                            ? t('top_percent', { percent: calculateTopPercentage(ranksSectionData?.total, ranksSectionData?.ranks?.[0]?.rank) })
                                            : membership?.division
                                                ? t('top_percent', { percent: calculateTopPercentage(ranksDivisionData?.total, ranksDivisionData?.ranks?.[0]?.rank) })
                                                : t('top_percent', { percent: calculateTopPercentage(ranksOrganizationData?.total, ranksOrganizationData?.ranks?.[0]?.rank) }))}
                                />
                            </Box>
                            <ProgressBar
                                rangeStart={0}
                                rangeEnd={toggleActive === 0
                                    ? (membership?.section ? monthlyRanksSectionData?.total : membership?.division ? monthlyRanksDivisionData?.total : monthlyRanksOrganizationData?.total)
                                    : (membership?.section ? ranksSectionData?.total : membership?.division ? ranksDivisionData?.total : ranksOrganizationData?.total)}
                                start={0}
                                end={toggleActive === 0
                                    ? (membership?.section
                                        ? monthlyRanksSectionData?.total - monthlyRanksSectionData?.ranks[0]?.rank + 1
                                        : membership?.division
                                            ? monthlyRanksDivisionData?.total - monthlyRanksDivisionData?.ranks[0]?.rank + 1
                                            : monthlyRanksOrganizationData?.total - monthlyRanksOrganizationData?.ranks[0]?.rank + 1)
                                    : (membership?.section
                                        ? ranksSectionData?.total - ranksSectionData?.ranks[0]?.rank + 1
                                        : membership?.division
                                            ? ranksDivisionData?.total - ranksDivisionData?.ranks[0]?.rank + 1
                                            : ranksOrganizationData?.total - ranksOrganizationData?.ranks[0]?.rank + 1)}
                                height="20px"
                                backgroundColor={theme.palette.figma.white[20]}
                                showCurrentIndicator={false}
                                progressColor={theme.palette.figma.time}
                                sx={{ alignSelf: 'stretch' }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* 순위 상세 정보 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5,
                        alignItems: 'center',
                        width: '100%',
                        mt: 2.5,
                        minHeight: '110px' // 최소 높이 설정으로 세부소속이 없어도 카드 크기 유지
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.25,
                            width: '100%',
                            padding: '10px 0',
                            minHeight: '90px' // 최소 높이 설정
                        }}
                    >
                        {data?.map((item, index) => {
                            if (!item?.data) return null;
                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        height: '22px',
                                        width: '100%',
                                        mb: '5px',
                                        minWidth: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            minWidth: 0,
                                            flex: 1,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                color: theme.palette.figma.white[60],
                                                fontWeight: 400,
                                                letterSpacing: '-0.42px',
                                                textAlign: 'left',
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {toDisplayString(item.label, '')}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                textAlign: 'right',
                                                letterSpacing: '-0.42px',
                                                color: theme.palette.text.main,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[item.data?.ranks?.[0]?.rank?.toLocaleString() || '-', t('ranking_card_rank_suffix')].filter(Boolean).join(' ')}
                                        </Typography>
                                    </Box>
                                    <BoxData
                                        data={t('top_percent', { percent: calculateTopPercentage(item.data?.total, item.data?.ranks?.[0]?.rank) })}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* 하단 텍스트 */}
                <Typography
                    sx={{
                        fontSize: '12px',
                        color: theme.palette.figma.white[60],
                        textAlign: 'center',
                        fontWeight: 400,
                        letterSpacing: '-0.36px',
                        mt: 2.5
                    }}
                >
                    {toggleActive === 0
                        ? t('as_of_month_year', { year: new Date().getFullYear(), month: new Date().getMonth() + 1 })
                        : t('based_on_cumulative_experience')}
                </Typography>


            </PaperCard>
            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onCancel={isConfirmPopup ? handleClosePopup : undefined}
                onConfirm={isConfirmPopup ? handleConfirmChangeMainOrganization : handleClosePopup}
                confirmText={isConfirmPopup ? t('change') : undefined}
            />
        </>
    );
};

export default RankingCard;
